import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import {
  ArrowLeft,
  Gauge,
  Search,
  ShieldAlert,
  Snowflake,
  Stethoscope,
  Sun,
  Zap,
} from "lucide-react";

import {
  fetchCategories,
  fetchEntryNodes,
  fetchFaults,
  scoreFault,
  searchTerms,
} from "@/faults-app/lib/db";

import { AppShell } from "@/faults-app/components/app-shell";
import { categoryIcon, faultIcon } from "@/faults-app/components/electric-icons";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      {
        title: "فاحص الأعطال الكهربائي الذكي",
      },
      {
        name: "description",
        content:
          "برنامج عربي تفاعلي لتشخيص الأعطال الكهربائية: المحركات، اللوحات، دوائر السيطرة، PLC، VFD، المولدات والتأريض خطوة بخطوة.",
      },
      {
        property: "og:title",
        content: "فاحص الأعطال الكهربائي الذكي",
      },
      {
        property: "og:description",
        content:
          "تشخيص تفاعلي للأعطال الكهربائية باللغة العربية مع مكتبة أعطال شاملة.",
      },
    ],
  }),

  component: Home,
});

function Home() {
  const [query, setQuery] = useState("");

  const navigate = useNavigate();

  const { data: categories = [] } = useQuery({
    queryKey: ["categories"],
    queryFn: fetchCategories,
  });

  const { data: entries = [] } = useQuery({
    queryKey: ["entries"],
    queryFn: fetchEntryNodes,
  });

  const { data: faults = [] } = useQuery({
    queryKey: ["faults"],
    queryFn: () => fetchFaults(),
  });

  const terms = searchTerms(query);

  const results =
    terms.length === 0
      ? []
      : faults
          .map((f) => ({
            f,
            s: scoreFault(f, terms),
          }))
          .filter((r) => r.s > 0)
          .sort((a, b) => b.s - a.s)
          .slice(0, 8);

  const plcCategory = categories.find((c) => {
    const slug = (c.slug ?? "").toLowerCase().trim();
    const name = (c.name_ar ?? "").trim();

    return (
      name === "PLC" ||
      name.includes("أعطال PLC") ||
      name.includes("اعطال PLC") ||
      slug === "plc" ||
      slug.includes("plc")
    );
  });

  const categoriesWithoutPlc = categories.filter(
    (c) => c.id !== plcCategory?.id,
  );

  const plcCount = plcCategory
    ? faults.filter((f) => f.category_id === plcCategory.id).length
    : 0;

  const PlcIcon = plcCategory
    ? categoryIcon(plcCategory.icon, plcCategory.name_ar)
    : Zap;

  const startDiagnose = () => {
    if (results[0]?.f.next_question_key) {
      navigate({
        to: "/diagnose",
        search: {
          node: results[0].f.next_question_key,
        },
      });

      return;
    }

    if (results[0]) {
      navigate({
        to: "/fault/$id",
        params: {
          id: results[0].f.id,
        },
      });

      return;
    }

    const first = entries[0];

    navigate({
      to: "/diagnose",
      search: {
        node: first ? first.node_key : "motor_not_running",
      },
    });
  };

  return (
    <AppShell>
      <main className="mx-auto w-full max-w-5xl px-4 pt-6">

        {/* الواجهة الترحيبية */}

        <section className="relative overflow-hidden rounded-3xl panel-dark p-5 shadow-card sm:p-8">
          <Zap className="pointer-events-none absolute -left-6 -top-6 h-40 w-40 text-primary/10" />

          <div className="relative flex flex-col items-center gap-4 text-center">

            {/* اللوغو */}

            <div className="grid h-24 w-24 place-items-center rounded-3xl bg-white p-2 shadow-card sm:h-28 sm:w-28">
              <img
                src="/faults-app/main-logo.png"
                alt="شعار فاحص الأعطال الكهربائي الذكي"
                className="h-full w-full object-contain"
              />
            </div>

            <h1 className="text-2xl font-extrabold text-white sm:text-4xl">
              فاحص الأعطال الكهربائي الذكي
            </h1>

            <p className="text-sm font-bold text-primary sm:text-base">
              شخّص العطل خطوة بخطوة
            </p>
          </div>

          <div className="relative mt-6 rounded-2xl bg-card p-3 shadow-card sm:p-4">
            <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">

              <div className="min-w-0">
                <label
                  className="mb-2 flex items-center gap-2 text-sm font-bold text-black"
                  htmlFor="q"
                >
                  <Search className="h-4 w-4 text-black" />

                  اكتب المشكلة التي تواجهك...
                </label>

                <textarea
                  id="q"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  rows={2}
                  placeholder="مثال: الموتور يسخن ويفصل الأوفرلود"
                  className="w-full resize-none rounded-xl border border-input bg-background p-3 text-base text-black caret-black outline-none transition placeholder:text-black/70 focus:border-primary focus:ring-3 focus:ring-primary/25"
                />
              </div>

              <button
                onClick={startDiagnose}
                className="press inline-flex h-13 items-center justify-center gap-2 rounded-xl bg-primary px-6 py-4 text-base font-extrabold text-primary-foreground shadow-glow transition hover:bg-primary-dark hover:text-white"
              >
                <Stethoscope className="h-5 w-5" />

                ابدأ التشخيص
              </button>
            </div>

            {results.length > 0 && (
              <ul className="mt-3 space-y-2">
                {results.map(({ f }) => {
                  const Ico = faultIcon(`${f.name_ar} ${f.device_ar}`);

                  return (
                    <li key={f.id}>
                      <Link
                        to="/fault/$id"
                        params={{
                          id: f.id,
                        }}
                        className="press flex items-center gap-3 rounded-xl border border-border p-3 transition-colors hover:border-primary hover:bg-accent"
                      >
                        <Ico className="h-5 w-5 shrink-0 text-primary-dark" />

                        <span className="min-w-0">
                          <span className="block truncate font-bold">
                            {f.name_ar}
                          </span>

                          <span className="block truncate text-xs text-muted-foreground">
                            {f.device_ar}
                          </span>
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}

            {query.trim().length > 2 && results.length === 0 && (
              <p className="mt-3 text-sm text-muted-foreground">
                لا توجد نتائج مطابقة، جرّب كلمات أخرى مثل: يسخن، يفصل، لا
                يجذب، لا يدور.
              </p>
            )}
          </div>
        </section>

        {/* فحص وتشخيص الأعطال المتخصص */}

        <section className="mt-8">
          <SectionTitle
            icon={<Gauge className="h-5 w-5" />}
            title="فحص وتشخيص الأعطال المتخصص"
          />

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Link
              to="/vfd-codes"
              className="card-tech press group flex items-center gap-3 p-4"
            >
              <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-linear-to-br from-primary to-primary-glow text-primary-foreground shadow-glow">
                <Gauge className="h-6 w-6" />
              </span>

              <span className="min-w-0">
                <span className="block text-base font-extrabold">
                  رموز أعطال VFD
                </span>

                <span className="block text-xs text-muted-foreground">
                  الشركة ← الموديل ← رمز العطل
                </span>
              </span>

              <ArrowLeft className="ms-auto h-5 w-5 shrink-0 text-primary-dark transition-transform group-hover:-translate-x-1" />
            </Link>

            <Link
              to="/ac-codes"
              className="card-tech press group flex items-center gap-3 p-4"
            >
              <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-linear-to-br from-primary to-primary-glow text-primary-foreground shadow-glow">
                <Snowflake className="h-6 w-6" />
              </span>

              <span className="min-w-0">
                <span className="block text-base font-extrabold">
                  رموز أعطال المكيفات Split AC
                </span>

                <span className="block text-xs text-muted-foreground">
                  أكواد المكيفات لكل شركة
                </span>
              </span>

              <ArrowLeft className="ms-auto h-5 w-5 shrink-0 text-primary-dark transition-transform group-hover:-translate-x-1" />
            </Link>

            <Link
              to="/solar"
              className="card-tech press group flex items-center gap-3 p-4"
            >
              <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-linear-to-br from-primary to-primary-glow text-primary-foreground shadow-glow">
                <Sun className="h-6 w-6" />
              </span>

              <span className="min-w-0">
                <span className="block text-base font-extrabold">
                  أعطال الطاقة الشمسية
                </span>

                <span className="block text-xs text-muted-foreground">
                  أسباب وخطوات فحص وقياسات وحلول
                </span>
              </span>

              <ArrowLeft className="ms-auto h-5 w-5 shrink-0 text-primary-dark transition-transform group-hover:-translate-x-1" />
            </Link>

            {plcCategory && (
              <Link
                to="/library"
                search={{
                  cat: plcCategory.slug,
                  q: "",
                }}
                className="card-tech press group flex items-center gap-3 p-4"
              >
                <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-linear-to-br from-primary to-primary-glow text-primary-foreground shadow-glow">
                  <PlcIcon className="h-6 w-6" />
                </span>

                <span className="min-w-0">
                  <span className="block text-base font-extrabold">
                    {plcCategory.name_ar}
                  </span>

                  <span className="block text-xs text-muted-foreground">
                    {plcCategory.description_ar ?? `${plcCount} عطل في المكتبة`}
                  </span>
                </span>

                <ArrowLeft className="ms-auto h-5 w-5 shrink-0 text-primary-dark transition-transform group-hover:-translate-x-1" />
              </Link>
            )}
          </div>
        </section>

        {/* بطاقات الأقسام */}

        <section className="mt-8">
          <SectionTitle
            icon={<Zap className="h-5 w-5" />}
            title="أقسام الأعطال"
          />

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {categoriesWithoutPlc.map((c) => {
              const Ico = categoryIcon(c.icon, c.name_ar);

              const count = faults.filter(
                (f) => f.category_id === c.id,
              ).length;

              return (
                <Link
                  key={c.id}
                  to="/library"
                  search={{
                    cat: c.slug,
                    q: "",
                  }}
                  className="card-tech press group flex flex-col p-4"
                >
                  <div className="flex items-start gap-3">
                    <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-linear-to-br from-primary to-primary-glow text-primary-foreground shadow-glow">
                      <Ico className="h-7 w-7" />
                    </span>

                    <span className="min-w-0">
                      <span className="block truncate text-lg font-extrabold">
                        {c.name_ar}
                      </span>

                      <span className="mt-1 line-clamp-2 block text-xs text-muted-foreground">
                        {c.description_ar ??
                          "أعطال وحلول واختبارات ميدانية"}
                      </span>
                    </span>
                  </div>

                  <div className="mt-4 flex items-center justify-between border-t border-border pt-3">
                    <span className="rounded-full bg-muted px-3 py-1 text-xs font-bold text-muted-foreground">
                      {count} عطل في المكتبة
                    </span>

                    <span className="inline-flex items-center gap-1 text-sm font-extrabold text-primary-dark">
                      فحص الأعطال

                      <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>

        {/* تشخيص سريع */}

        <section className="mt-8">
          <SectionTitle
            icon={<ShieldAlert className="h-5 w-5" />}
            title="ابدأ تشخيصاً تفاعلياً"
          />

          <div className="grid gap-3 sm:grid-cols-2">
            {entries.map((n) => {
              const Ico = faultIcon(n.title_ar ?? n.question_ar);

              return (
                <button
                  key={n.id}
                  onClick={() =>
                    navigate({
                      to: "/diagnose",
                      search: {
                        node: n.node_key,
                      },
                    })
                  }
                  className="card-tech press flex items-center gap-3 p-4 text-right"
                >
                  <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-accent text-accent-foreground">
                    <Ico className="h-6 w-6" />
                  </span>

                  <span className="min-w-0 flex-1 text-base font-bold">
                    {n.title_ar}
                  </span>

                  <ArrowLeft className="h-5 w-5 shrink-0 text-primary-dark" />
                </button>
              );
            })}
          </div>
        </section>

      </main>
    </AppShell>
  );
}

function SectionTitle({
  icon,
  title,
}: {
  icon: React.ReactNode;
  title: string;
}) {
  return (
    <h2 className="mb-3 flex items-center gap-2 text-lg font-extrabold">
      <span className="grid h-8 w-8 place-items-center rounded-lg bg-steel text-primary">
        {icon}
      </span>

      {title}

      <span className="ms-2 h-px flex-1 bg-border" />
    </h2>
  );
}